import { ADMIN_REFUND_REASONS } from '@carcheck/shared';
import { z } from 'zod';

import type { StatusOption } from '../../../components/filters/StatusMultiSelect';

/**
 * URL filter schema for the refunds history list (AUT-129). Mirrors the
 * backend `ListAdminRefundsQuerySchema` — `reason` is a multi-select over
 * the FULL six `ADMIN_REFUND_REASONS` (architect guardrail #2: the history
 * can contain ANY reason, including the listener-emitted ones, so the
 * FILTER offers all six — only the operator-initiated ISSUE action is
 * narrowed to `admin_manual`), `orderId` is an exact-match UUID, `dateFrom`
 * / `dateTo` are ISO date strings binding the backend's `createdAt` filter.
 *
 * UNLIKE the payouts triage queue, there is NO default `reason` filter —
 * this is a history list, so the operator lands on everything. There is NO
 * `q` free-text filter (not in the backend query schema).
 *
 * Every key has a `.catch` default so a malformed / absent query param
 * degrades safely instead of throwing.
 */

/**
 * `reason=admin_manual,dispute_partial` → string[] of valid reasons.
 * Absent or empty → `[]` (no filter — the full history).
 */
const reasonSchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) => {
    if (raw === undefined) return [] as (typeof ADMIN_REFUND_REASONS)[number][];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is (typeof ADMIN_REFUND_REASONS)[number] =>
        (ADMIN_REFUND_REASONS as readonly string[]).includes(s),
      );
  });

const orderIdSchema = z.string().optional().catch(undefined);
const dateSchema = z.string().optional().catch(undefined);

export const refundFiltersSchema = z.object({
  reason: reasonSchema,
  orderId: orderIdSchema,
  dateFrom: dateSchema,
  dateTo: dateSchema,
});

export type RefundFilters = z.infer<typeof refundFiltersSchema>;

/** Reason filter options (multi-select) — labels resolved via i18n keys. */
export const REASON_OPTIONS: StatusOption[] = ADMIN_REFUND_REASONS.map((value) => ({
  value,
  labelKey: `refunds.reason.${value}`,
}));

/** True when any managed filter is active (drives the Clear-filters link). */
export const hasActiveFilters = (filters: RefundFilters): boolean =>
  (filters.reason?.length ?? 0) > 0 ||
  Boolean(filters.orderId && filters.orderId.length > 0) ||
  Boolean(filters.dateFrom && filters.dateFrom.length > 0) ||
  Boolean(filters.dateTo && filters.dateTo.length > 0);
